import React, { useCallback, useEffect, useState } from "react";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import {
  Badge,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import { english } from "flatpickr/dist/l10n/default";
import "flatpickr/dist/themes/light.css";
import { Link } from "react-router-dom";
import usePermissions from "src/hooks/usePermissions";
import { getTicketStats } from "src/helpers/backend_helper";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Doughnut, Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

type StatsData = {
  total_tickets: number;
  open_tickets: number;
  in_progress_tickets: number;
  resolved_tickets: number;
  sla_first_response_breached: number;
  sla_resolution_breached: number;
  avg_first_response_minutes: number;
  avg_resolution_minutes: number;
  avg_satisfaction_rating: number;
  total_ratings: number;
  by_status: Record<string, number>;
  by_priority: Record<string, number>;
  by_category: Record<string, number>;
  top_assignees: Array<{ name: string; total: number; avg_resolution: number | null }>;
  daily_trend: Array<{ date: string; created: number }>;
};

const STATUS_COLORS: Record<string, string> = {
  open: "#f1b44c",
  in_progress: "#50a5f1",
  resolved: "#34c38f",
  closed: "#74788d",
};

const PRIORITY_LABELS: Record<string, string> = {
  "1": "Critical",
  "2": "High",
  "3": "Medium",
  "4": "Low",
};

const PRIORITY_COLORS = ["#f46a6a", "#f1b44c", "#50a5f1", "#74788d"];

const formatMinutes = (minutes: number): string => {
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

const MetricCard = ({
  title,
  value,
  color = "primary",
  subtitle,
}: {
  title: string;
  value: string | number;
  color?: string;
  subtitle?: string;
}) => (
  <Card className="mini-stats-wid">
    <CardBody>
      <div className="d-flex">
        <div className="flex-grow-1">
          <p className="text-muted fw-medium mb-2">{title}</p>
          <h4 className={`mb-0 text-${color}`}>{value}</h4>
          {subtitle && <small className="text-muted">{subtitle}</small>}
        </div>
      </div>
    </CardBody>
  </Card>
);

const TicketDashboardPage = () => {
  document.title = "Ticket Analytics | Dashboard";

  const { hasPermission } = usePermissions();
  const canView = hasPermission("view-tickets");

  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState<StatsData | null>(null);
  const [dateFrom, setDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [dateTo, setDateTo] = useState(() => new Date().toISOString().slice(0, 10));

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const resp: any = await getTicketStats({ date_from: dateFrom, date_to: dateTo });
      setStats(resp?.data ?? null);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (canView) {
      void loadStats();
    }
  }, [canView, loadStats]);

  if (!canView) {
    return (
      <div className="page-content">
        <Container fluid>
          <p className="text-danger">No access</p>
        </Container>
      </div>
    );
  }

  const statusChartData = stats
    ? {
        labels: Object.keys(stats.by_status).map(
          (s) => s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ")
        ),
        datasets: [
          {
            data: Object.values(stats.by_status),
            backgroundColor: Object.keys(stats.by_status).map(
              (s) => STATUS_COLORS[s] || "#ccc"
            ),
          },
        ],
      }
    : null;

  const priorityChartData = stats
    ? {
        labels: Object.keys(stats.by_priority).map(
          (p) => PRIORITY_LABELS[p] || `P${p}`
        ),
        datasets: [
          {
            data: Object.values(stats.by_priority),
            backgroundColor: Object.keys(stats.by_priority).map(
              (_, i) => PRIORITY_COLORS[i] || "#ccc"
            ),
          },
        ],
      }
    : null;

  const categoryChartData = stats && Object.keys(stats.by_category).length > 0
    ? {
        labels: Object.keys(stats.by_category),
        datasets: [
          {
            label: "Tickets",
            data: Object.values(stats.by_category),
            backgroundColor: "#556ee6",
          },
        ],
      }
    : null;

  const trendChartData = stats && stats.daily_trend.length > 0
    ? {
        labels: stats.daily_trend.map((d) => d.date),
        datasets: [
          {
            label: "Tickets created",
            data: stats.daily_trend.map((d) => d.created),
            borderColor: "#556ee6",
            backgroundColor: "rgba(85, 110, 230, 0.1)",
            fill: true,
            tension: 0.3,
          },
        ],
      }
    : null;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Support" breadcrumbItem="Ticket Analytics" />

          <Row className="mb-3">
            <Col md={12} className="d-flex gap-2 align-items-end justify-content-between">
              <div className="d-flex gap-2 align-items-end">
                <div>
                  <Label className="form-label mb-1">From</Label>
                  <Flatpickr
                    className="form-control form-control-sm"
                    value={dateFrom}
                    options={{ dateFormat: "Y-m-d", locale: english }}
                    onChange={([date]: Date[]) => {
                      if (date) setDateFrom(date.toISOString().slice(0, 10));
                    }}
                  />
                </div>
                <div>
                  <Label className="form-label mb-1">To</Label>
                  <Flatpickr
                    className="form-control form-control-sm"
                    value={dateTo}
                    options={{ dateFormat: "Y-m-d", locale: english }}
                    onChange={([date]: Date[]) => {
                      if (date) setDateTo(date.toISOString().slice(0, 10));
                    }}
                  />
                </div>
                <Button size="sm" color="primary" onClick={() => void loadStats()} disabled={loading}>
                  {loading ? "Loading..." : "Apply"}
                </Button>
              </div>
              <Link to="/tickets" className="btn btn-light btn-sm">
                Back to tickets
              </Link>
            </Col>
          </Row>

          {loading && !stats && (
            <div className="text-center py-5">
              <Spinner color="primary" />
            </div>
          )}

          {stats && (
            <>
              {/* Metric cards */}
              <Row>
                <Col md={3}>
                  <MetricCard title="Total tickets" value={stats.total_tickets} color="primary" />
                </Col>
                <Col md={3}>
                  <MetricCard title="Open" value={stats.open_tickets} color="warning" />
                </Col>
                <Col md={3}>
                  <MetricCard title="In progress" value={stats.in_progress_tickets} color="info" />
                </Col>
                <Col md={3}>
                  <MetricCard title="Resolved / Closed" value={stats.resolved_tickets} color="success" />
                </Col>
              </Row>

              <Row>
                <Col md={3}>
                  <MetricCard
                    title="Avg first response"
                    value={formatMinutes(stats.avg_first_response_minutes)}
                    color="primary"
                  />
                </Col>
                <Col md={3}>
                  <MetricCard
                    title="Avg resolution time"
                    value={formatMinutes(stats.avg_resolution_minutes)}
                    color="primary"
                  />
                </Col>
                <Col md={3}>
                  <MetricCard
                    title="SLA breaches"
                    value={stats.sla_first_response_breached + stats.sla_resolution_breached}
                    color={stats.sla_first_response_breached + stats.sla_resolution_breached > 0 ? "danger" : "success"}
                    subtitle={`Response: ${stats.sla_first_response_breached}, Resolution: ${stats.sla_resolution_breached}`}
                  />
                </Col>
                <Col md={3}>
                  <MetricCard
                    title="Avg CSAT"
                    value={stats.total_ratings > 0 ? `${stats.avg_satisfaction_rating} / 5` : "—"}
                    color="primary"
                    subtitle={`${stats.total_ratings} ratings`}
                  />
                </Col>
              </Row>

              {/* Charts */}
              <Row className="g-3">
                {trendChartData && (
                  <Col xl={8}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">Daily ticket trend</h5>
                        <Line
                          data={trendChartData}
                          options={{
                            responsive: true,
                            plugins: { legend: { display: false } },
                            scales: {
                              y: { beginAtZero: true, ticks: { stepSize: 1 } },
                            },
                          }}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {statusChartData && (
                  <Col xl={4}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">By status</h5>
                        <Doughnut
                          data={statusChartData}
                          options={{
                            responsive: true,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {priorityChartData && (
                  <Col xl={4}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">By priority</h5>
                        <Doughnut
                          data={priorityChartData}
                          options={{
                            responsive: true,
                            plugins: { legend: { position: "bottom" } },
                          }}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {categoryChartData && (
                  <Col xl={4}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">By category</h5>
                        <Bar
                          data={categoryChartData}
                          options={{
                            responsive: true,
                            indexAxis: "y",
                            plugins: { legend: { display: false } },
                            scales: {
                              x: { beginAtZero: true, ticks: { stepSize: 1 } },
                            },
                          }}
                        />
                      </CardBody>
                    </Card>
                  </Col>
                )}

                {stats.top_assignees.length > 0 && (
                  <Col xl={4}>
                    <Card>
                      <CardBody>
                        <h5 className="card-title mb-3">Top assignees</h5>
                        <Table responsive size="sm" className="mb-0">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th className="text-center">Tickets</th>
                              <th className="text-end">Avg resolution</th>
                            </tr>
                          </thead>
                          <tbody>
                            {stats.top_assignees.map((a, i) => (
                              <tr key={i}>
                                <td>{a.name}</td>
                                <td className="text-center">
                                  <Badge color="primary">{a.total}</Badge>
                                </td>
                                <td className="text-end">
                                  {a.avg_resolution
                                    ? formatMinutes(Math.round(Number(a.avg_resolution) / 60))
                                    : "—"}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </CardBody>
                    </Card>
                  </Col>
                )}
              </Row>
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default TicketDashboardPage;
